import type { IdentificationDocumentType } from '@/modules/shared/types/IdentificationDocumentType';
import { GtmEventInteractiveAction } from '@/modules/shared/utils/gtmEvents/types';

export interface IGTMEvent {
  'event-interactive-category'?: string;
  'event-interactive-accion'?: string;
  'event-interactive-tag'?: string;
  'event-interactive-value'?: string;
  'event-interactive-metodoGuardado'?: boolean;
  'event-interactive-cuotas'?: number;
  event: string;
}
export interface IGtmPageVirtualEvent {
  event: GtmEvent | string;
  'type-User': GtmTypeUser | string;
  page: GtmPagePath | string;
  pagename: GtmPageName | string;
}

type IGtmEcommercePurchase = {
  actionField: {
    action: string;
    affiliation: string;
    coupon: string;
    currency: string;
    id: string;
    option: string;
    revenue: number;
    shipping: number;
    step: string;
    tax: number;
  };
  products: (IGtmProductBilling | IWebViewGtmProductBilling)[];
  purchaseParams?: IGtmEcommercePurchaseWebView;
  documentType?: IdentificationDocumentType;
};

type IGtmEcommercePurchaseWebView = {
  AnalyticsParameterAffiliation?: string;
  AnalyticsParameterCurrency?: string;
  AnalyticsParameterValue?: number;
  AnalyticsParameterCoupon?: string;
  AnalyticsParameterShippingTier?: string | null;
  AnalyticsParameterPaymentType?: string;
  AnalyticsParameterShipping?: number;
  AnalyticsParameterTax?: number;
};

export interface IGTMSendProductsBillingPurchase {
  event: string;
  'type-User': GtmTypeUser;
  'payment-method'?: string;
  ecommerce: {
    purchase: IGtmEcommercePurchase;
  };
}

export interface IGTMSendProductsBillingPurchaseWebView {
  event: string;
  'type-User': GtmTypeUser;
  'payment-method': string;
  ecommerce: {
    purchase?: IGtmEcommercePurchase;
    purchaseParams: IGtmEcommercePurchaseWebView;
    checkout?: {
      checkoutParams: IGtmEcommercePurchaseWebView;
    };
  };
}

export interface IGTMSendProductsBilling {
  event: string;
  'type-User': GtmTypeUser;
  ecommerce: {
    purchase?: IGtmEcommercePurchase;

    checkout?: {
      actionField: {
        step: string;
        option: string;
        action?: GtmEventInteractiveAction;
      };
      paymentParams?: IGtmEcommercePurchaseWebView;
      products: (IWebViewGtmProductBilling | IGtmProductBilling)[];
      documentType?: IdentificationDocumentType;
    };
  };
}

export interface IGTMGamificationEvent {
  'event-interactive-category': string;
  'event-interactive-accion': string;
  'event-interactive-tag': string;
  'event-interactive-nivel'?: string;
  'event-interactive-puntos'?: string;
  event: string;
}

export interface IGTMPurchaseDetailEvent extends IGTMEvent {
  'event-interactive-ordenCompra': string;
  'event-interactive-Sku': string;
  'event-interactive-fechaCompra': string;
  'event-interactive-fechaCompromiso': string;
  'event-interactive-estadoCompra': string;
  'event-interactive-metodoPago': string;
}
export interface IGTMRefundEvent extends IGTMEvent {
  'event-interactive-Sku': string;
  'event-interactive-metodoPago': string;
  'event-interactive-motivoDevolución'?: string;
  'event-interactive-cantidadUnidades'?: string;
  'event-interactive-fechaDevolución'?: string;
  'event-interactive-estadoDevolución'?: string;
}

enum GtmEventInteractiveCategory {
  MyProfile = 'Mi perfil',
  CardsAndReimburseInfo = 'Tarjeta y datos para reembolso',
  Favorites = 'Favoritos',
  Wallet = 'Mi monedero',
  CencosudCard = 'Tarjeta Cencosud',
  GiftCards = 'Gift cards',
  HelpCenter = 'Centro de ayuda',
  Gamification = 'Gamification',
  Cart = 'Carro',
  SaveForLater = 'Guardar para después',
  WarrantyExtended = 'Garantía extendida',
  AddWarranty = 'Add Garantía',
  ArrowCarousel = 'Flechas recomendaciones',
  SwatchesCart = 'Swatches carro',
  TableSize = 'Tabla de talla',
  Refund = 'Flujo devolución',
  RefundForm = 'Flujo de devolución formulario',
  RefundDetail = 'Detalle devolución compra',
  Guest = 'Mis compras Guest',
  RecoveryPassword = 'Recuperar contraseña',
  Sampling = 'Sapling',
  GuestFollow = 'Seguimiento mis compras Guest',
  PdpSizesGuide = 'pdp tabla de tallas',
  PlpPaginator = 'Páginador PLP',
  SwatchesPlp = 'swatches plp',
  BillingPayment = 'Checkout pago',
  BillingInvoiceModal = 'Modal borrar datos facturación',
  BillingBuyConfirmation = 'Interacciones confirmación de compra',
  BillingBuyCencoPay = 'Clic botón continuar con CencoPay',
  BillingCloseCencoPayModal = 'Clic botón cerrar modal de pago CencoPay',
  BillingSelectOtherPaymentMethod = 'Clic botón elegir otro medio de pago en modal de pago CencoPay',
  BillingGenerateNewCode = 'Clic botón generar un código nuevo en modal de pago CencoPay',
  BillingExpiredQR = 'Mensaje Código QR ya expiro',
  BillingcencoPayConnectError = 'Respuesta cencopay - modal hay problemas de conexión',
  BillingcencoPayTryOtherPayment = 'Clic botón intentar con otro medio de pago - modal hay problemas de conexión',
  BillingcencoPayModalGoBack = 'Clic botón volver - modal pagar con cencopay',
  BillingCencoPayModalChange = 'Clic botón cambiar - modal pagar con cencopay',
  BillingPayWithInvoiceModal = 'modal ¿quieres pagar con factura?'
}

enum GtmEventInteractiveTag {
  PersonalData = 'Datos personales',
  ChangePassword = 'Cambio de contraseña',
  Addresses = 'Direcciones',
  SaveButton = 'Clic botón guardar',
  DoItLaterButton = 'Clic botón hacerlo después',
  PersonalDataError = 'No pudimos agregar tu información',
  EditLaterButton = 'Clic botón Editar después',
  ChangePasswordError = 'No pudimos guardar tu nueva contraseña',
  AddressesAddAddressButton = 'Clic botón agregar dirección',
  AddressesAddError = 'No pudimos agregar tu nueva dirección',
  AddressesEditError = 'No pudimos actualizar tu dirección',
  AddressesDeleteError = 'No pudimos eliminar tu dirección',
  AddressesEditAddressButton = 'Clic botón Editar dirección guardada',
  AddressesDeleteAddressButton = 'Clic botón Eliminar dirección guardada',
  AddressSaveFavoriteLocationButton = 'Clic botón ubicación favorita',
  AddressSaveFavoriteLocationError = 'No pudimos actualizar tu dirección como favorita',
  AddressAddNewAddressButton = 'Clic botón agregar una nueva dirección',
  SavedCards = 'Tarejtas guardadas',
  ReimburseBankAccount = 'Cuenta bancaria para reembolso',
  SavedCardsNoSavedCards = 'Aún no tienes tarjetas guardadas',
  SavedCardsDeleteCardButton = 'Clic Eliminar tarjeta',
  SavedCardsCardDeleted = 'Tarjeta eliminada',
  SavedCardsModalBackButton = 'Clic volver',
  SavedCardsDeleteCardError = 'No pudimos eliminar tu tarjeta',
  ReimburseCardNoCard = 'Aún no tienes una cuenta guardada',
  ReimburseCardAddCardButton = 'Clic agregar una cuenta bancaria',
  ReimburseCardSaveCardButton = 'Datos guardados con éxito',
  ReimburseCardFormEditLaterButton = 'Clic agregar después',
  ReimburseCardSaveCardError = 'No pudimos agregar tu cuenta bancaria para reembolso',
  ReimburseCardEditLaterButton = 'Clic editar en otro momento',
  ReimburseCardEditInfoButton = 'Clic editar información',
  Favorites = 'Favoritos',
  FavoritesEmpty = 'Aún no tienes productos favoritos',
  FavoritesFilterAvailable = 'Disponible',
  FavoritesSortingRecent = 'Ordenar por: agregados recientemente',
  FavoritesSortingHigherPrice = 'Ordenar por: Precio de mayor a menor',
  FavoritesSortingLowerPrice = 'Ordenar por: Precio de menor a mayor',
  FavoritesPagination = 'Paginador de favoritos',
  GamificationShowMoreChallenges = 'Botón mostrar más desafíos',
  Wallet = 'Mi monedero',
  CencosudCardRequestCencosudCard = 'Solicita tu tarjeta',
  CencosudCardPayCencosudCardbill = 'Pagar tu tarjeta',
  CencosudCardSimulateAdvance = 'Simula un avance',
  CencosudCardSimulateSuperAdvance = 'Simula un Súper Avance',
  CencosudCardPayLoan = 'Paga tu crédito de consumo',
  GiftCards = 'Gift cards',
  HelpCenter = 'Centro de ayuda',
  ContinuePayment = 'Continuar con el pago',
  ArrowLeft = 'Flecha izquierda',
  ArrowRight = 'Flecha derecha',
  OrderNumber = 'Número de orden',
  EmailAddress = 'Correo Electrónico',
  RecoveryPasswordButton = 'Clic',
  RecoveryPasswordSuccess = 'Recuperación exitosa',
  RecoveryPasswordError = 'Datos Erróneos',
  GuestOpenModal = 'Clic Apertura modal OC',
  GuestClickLoginButton = 'Inicia Sesion o registrate',
  GuestClickLinkTest = 'Seguimiento por Correo Electronico',
  GuestCleanSearcherOC = '/seguimiento/mis-compras/orden-de-compra/',
  //   EmailTrackingGuest = 'Seguimiento por Correo Electronico',
  ForgotPassword = 'Olvidaste tu correo',
  SelectSampling = 'Elegir muestras',
  BillingGiftCardFindIt = 'Clic link donde lo encuentro',
  BillingGiftCardFindItOk = 'Clic botón entendido modal donde lo encuentro',
  BillingRemoveGiftCard = 'Clic botón eliminar tarjeta gift card',
  BillingContinueWithWebpay = 'Continuar con webpay',
  BillingShowLessClick = 'Click link mostrar menos',
  BillingShowMoreClick = 'Click link mostrar más',
  BillingRegisteredUser = 'usuario registrado',
  BillingGuestUser = 'usuario guest',
  BillingSelectOtherPaymentMethodWithGiftCard = 'Clic botón elegir otro medio de pago',
  BillingClickIconX = 'Clic icono x'
}

enum GtmEvent {
  EventInteractive = 'event-interactive',
  PageVirtual = 'page-virtual',
  AddtoCart = 'addToCart',
  ECCartProducts = 'eC-cartProducts',
  plpProductView = 'plpProductView',
  plpProductClick = 'plpProductClick',
  EventUserIdentification = 'event-identificacion-usuario'
}

enum GtmTypeUser {
  Guest = 'guest',
  Registered = 'authenticated'
}

enum GtmPagePath {
  AccountHome = '/mi-cuenta',
  Addresses = '/mi-cuenta/direcciones',
  Favorites = '/mi-cuenta/favoritos',
  PersonalData = '/mi-cuenta/informacion-personal',
  MyCredits = '/mi-cuenta/mis-creditos',
  Purchases = '/mi-cuenta/mis-compras',
  PurchasesByOrderNumber = '/mi-cuenta/mis-compras/:orderNumber',
  PurchasesGuest = '/seguimiento/mis-compras/orden-de-compra',
  PurchasesGuestByOrderNumber = '/seguimiento/mis-compras/orden-de-compra/:orderNumber',
  ChangePassword = '/mi-cuenta/cambio-contrasena',
  RequestCencosudCard = '/mi-cuenta/solicitar-tarjeta',
  PayCencosudCardBill = '/mi-cuenta/pagar-tarjeta',
  SimulateAdvance = '/mi-cuenta/simular-avance',
  SimulateSuperAdvance = '/mi-cuenta/simular-super-avance',
  PayLoan = '/mi-cuenta/pagar-credito-consumo',
  GiftCards = '/mi-cuenta/giftcards',
  HelpCenter = '/mi-cuenta/centro-ayuda',
  ReimburseCard = '/mi-cuenta/datos-bancarios',
  PurchaseDetail = '/mi-cuenta/mis-compras/:orderNumber/:sku',
  Home = '/',
  SavedCards = '/mi-cuenta/tarjetas-guardadas',
  Cart = '/cart',
  RefundDetails = '/mi-cuenta/devolucion/:orderNumber',
  TrackingPurchases = 'seguimiento/mis-compras/email',
  Billing = '/billing/payment',
  CheckoutConfirmation = '/checkout/confirmation'
}

enum GtmPageName {
  MyAccount = 'Mi Cuenta',
  MyProfile = 'Mi perfil',
  PersonalData = 'Datos personales',
  ChangePassword = 'Cambio contraseña',
  Addresses = 'Direcciones ',
  Purchases = 'Mis compras',
  PurchasesGuest = 'Seguimiento Mis compras',
  PurchaseDetail = 'Detalle de compra',
  MyCredits = 'Club Paris',
  SavedCards = 'Tarjetas guardadas',
  ReimburseCard = 'Cuenta bancaria para reembolso',
  Favorites = 'Favoritos',
  RequestCencosudCard = 'Solicita tu tarjeta',
  PayCencosudCardBill = 'Paga tu tarjeta',
  SimulateAdvance = 'Simula un avance',
  SimulateSuperAdvance = 'Simula un Súper Avance',
  PayLoan = 'Paga tu crédito de consumo',
  HelpCenter = 'Centro de ayuda',
  GiftCards = 'Gift cards',
  Cart = 'Cart',
  RefundDetail = 'Detalle devolución',
  Billing = 'Buying Process - Billing Selector',
  CheckoutConfirmation = 'Buy Process - Confirmation',
  FastCheckoutConfirmation = 'Buy Process - Confirmation Fast Checkout'
}

export type { IGtmEventProductCart };

export {
  GtmEvent,
  GtmEventInteractiveAction,
  GtmEventInteractiveCategory,
  GtmEventInteractiveTag,
  GtmPageName,
  GtmPagePath,
  GtmTypeUser
};

export interface IGMDimension {
  dimension2?: string;
  dimension3?: string;
  dimension32?: string;
  dimension11?: string;
  dimension33?: string;
  dimension36?: string;
  dimension12?: string;
  dimension18?: string;
  dimension19?: string;
  dimension20?: number;
  dimension30?: string;
  dimension41?: string;
  dimension42?: number;
  dimension21?: boolean;
  dimension76?: boolean;
  dimension77?: number;
  dimension17?: string;
}
export interface IGtmProductCart extends IGMDimension {
  id: string;
  name: string;
  variant: string;
  category: string;
  brand: string;
  price: string;
  quantity?: number;
  list?: string;
  position?: number;
  servicio?: string;
}

export interface IGtmProductBilling extends IGMDimension {
  id: string;
  name: string;
  variant: string;
  category: string;
  list?: string;
  brand: string;
  price: string;
  quantity: number;
}

export interface IWebViewGtmProductBilling {
  AnalyticsParameterItemID: string;
  SearchParameterItemID: string;
  AnalyticsParameterItemName: string;
  AnalyticsParameterItemCategory: string;
  AnalyticsParameterItemCategory2: string;
  AnalyticsParameterItemCategory3: string;
  AnalyticsParameterItemCategory4: string;
  AnalyticsParameterItemCategory5: string;
  AnalyticsParameterItemVariant?: string;
  AnalyticsParameterItemBrand: string;
  AnalyticsParameterPrice: string;
  AnalyticsParameterQuantity: number;
  tipo_envio: string;
}

interface IGtmEventProductEcAdd {
  add?: {
    products: IGtmProductCart[];
  };
}

interface IGtmEventProductEcClick {
  click?: {
    actionField: {
      //   list: TNameCarousel | string;
      action: string;
    };
    products: IGtmProductCart[];
  };
}

interface IGtmEventProductEcCheckout {
  checkout?: {
    actionField: {
      step: string;
      option: string;
      action: GtmEventInteractiveAction;
    };
    products:
      | IGtmProductCart[]
      | IWebViewGtmProductBilling[]
      | IGtmProductBilling[];
  };
}

export interface IGtmEventProductImpressions {
  impressions: IGtmProductCart[];
}

interface IGtmEventProductCart {
  event: string;
  'type-User'?: GtmTypeUser | string;
  ecommerce:
    | ({
        currencyCode?: string;
      } & IGtmEventProductEcAdd)
    | IGtmEventProductEcCheckout
    | IGtmEventProductImpressions
    | IGtmEventProductEcClick;
}
